import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import axios from 'axios';
import office365 from '../../assets/images/office365.png';
import googleImage from '../../assets/images/google.png';

const MfaAuthModal = forwardRef((props, ref) => {
  const [showModal, setShowModal] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [mode, setMode] = useState('mfa');
  const [ssoScope, setSsoScope] = useState('');
  const [ssoUrl, setSsoUrl] = useState('');
  const resolveRef = useRef(null);
  const contextRef = useRef(null);

  const closeModal = (result = false) => {
    if (resolveRef.current) {
      resolveRef.current(result);
      resolveRef.current = null;
    }
    contextRef.current = null;
    setShowModal(false);
    setPasscode('');
    setError('');
    setMode('mfa');
    setSsoScope('');
    setSsoUrl('');
  };

  const startSso = async () => {
    const ctx = contextRef.current;
    if (!ctx) return;
    try {
      let url = ssoUrl;
      if (!url) {
        const payload = { cloud_id: ctx.cloudId };
        const apiUrl = `${process.env.REACT_APP_ADMIN_API_URL}getClientIds`;
        // alert(
        //   `[getClientIds] request\nPOST ${apiUrl}\n\nPayload:\n${JSON.stringify(payload, null, 2)}`
        // );
        const response = await axios.post(apiUrl, payload);
        // alert(
        //   `[getClientIds] response\nstatus: ${response.status}\n\ndata:\n${JSON.stringify(response.data, null, 2)}`
        // );
        const baseURL = window.location.origin;
        if (ssoScope === 'google') {
          const googleClientId = response.data.google_clientid;
          if (!googleClientId) {
            setError('Google SSO is not configured');
            return;
          }
          const nonce = Math.random().toString(36).substring(2) + Date.now().toString(36);
          url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${googleClientId}&redirect_uri=${baseURL}/authCallback&response_type=id_token&scope=profile email&nonce=${nonce}&state=${ctx.cloudId}@google`;
        } else if (ssoScope === 'office' || ssoScope === 'microsoft') {
          const microsoftClientId = response.data.office365_clientid;
          if (!microsoftClientId) {
            setError('Microsoft SSO is not configured');
            return;
          }
          url = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${microsoftClientId}&response_type=token&redirect_uri=${baseURL}/authcallback&scope=openid profile email user.read&response_mode=fragment&state=${ctx.cloudId}@office`;
        } else {
          setError('Unsupported SSO scope');
          return;
        }
        setSsoUrl(url);
      }
      window.open(url, '_blank');
      setError('');
    } catch (err) {
      // const status = err?.response?.status;
      // const body = err?.response?.data;
      // alert(
      //   `[getClientIds] error\n${err.message}${status != null ? `\nstatus: ${status}` : ''}${
      //     body != null ? `\n\ndata:\n${JSON.stringify(body, null, 2)}` : ''
      //   }`
      // );
      const msg = err?.response?.data?.message || err.message;
      setError("[startSso] "+msg || 'Unable to start SSO authentication');
    }
  };

  const submit = async () => {
    if (mode === 'sso') {
      closeModal(true);
      return;
    }
    const code = (passcode || '').trim();
    if (!code) {
      setError('Authenticator code is required');
      return;
    }

    const ctx = contextRef.current;
    if (!ctx) return;
    let myPassword = atob(decodeURIComponent(ctx.password || ''));
    if(myPassword.startsWith('V2=')) {
      //remove the V2= prefix
      myPassword = myPassword.substring(3);
      myPassword = atob(myPassword);
    }
    const payload = {
      cloud_id: ctx.cloudId,
      username: ctx.username,
      password: myPassword || '',
      passcode: code,
      mfa_type: ctx.mfaType,
      mfa_vendor: ctx.mfaVendor,
      access_token: ctx.accessToken
    };

    try {
      await axios.post(`${process.env.REACT_APP_ADMIN_API_URL}secondFactorAppSignin`, payload);
      closeModal(true);
    } catch (mfaErr) {
      const mfaMessage = mfaErr?.response?.data?.message || mfaErr.message;
      setError(mfaMessage || 'MFA verification failed');
      setPasscode('');
    }
  };

  useImperativeHandle(ref, () => ({
    openAndAuthenticate: (authContext) =>
      new Promise((resolve) => {
        contextRef.current = authContext;
        resolveRef.current = resolve;
        setPasscode('');
        setError('');
        setMode(authContext?.authType === 'sso' ? 'sso' : 'mfa');
        setSsoScope((authContext?.scope || '').toLowerCase());
        setShowModal(true);
      })
  }));

  if (!showModal) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          width: '90%',
          maxWidth: '420px',
          padding: '20px'
        }}
      >
        <h5 style={{ marginBottom: '12px' }}>
          {mode === 'sso' ? 'Single Sign-On Required' : 'Authenticator Verification'}
        </h5>
        {mode === 'sso' ? (
          <div>
            <div style={{ fontSize: '14px', color: '#444', marginBottom: '14px', textAlign: 'center' }}>
              {ssoScope === 'google' ? 'Continue with Google in a new tab.' : 'Continue with Microsoft in a new tab.'}
            </div>
            <div className="mt-3 mb-0 office365-signin">
              <button className="btn text-center btn-block flex w-full" type="button" onClick={startSso}>
                {ssoScope === 'google' ? (
                  <>
                    <img width="24" src={googleImage} alt="google" style={{ marginRight: '10px' }} />
                    <div className="flex justify-center items-center w-full"> Sign in with Google</div>
                  </>
                ) : (
                  <>
                    <img width="24" src={office365} alt="office365" style={{ marginRight: '10px' }} />
                    <div className="flex justify-center items-center w-full"> Sign in with Office 365</div>
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <input
            type="text"
            className="form-control"
            placeholder="Enter authenticator code"
            value={passcode}
            onChange={(e) => {
              setPasscode(e.target.value);
              setError('');
            }}
          />
        )}
        {error ? (
          <div style={{ color: '#dc3545', marginTop: '10px', fontSize: '14px' }}>{error}</div>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
          <button type="button" className="btn btn-secondary" onClick={() => closeModal(false)}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={submit}>
            {mode === 'sso'
              ? 'Done'
              : 'Verify'}
          </button>
        </div>
      </div>
    </div>
  );
});

export default MfaAuthModal;
